import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";

import { CircularProgress, Grid, MenuItem, SelectChangeEvent, Tooltip } from "@mui/material";
import classnames from "classnames";
import { parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useAgentHook } from "../../hooks/useAgentHook";
import { Location } from "../../../../../models/location";
import { Status, AgentStatus } from "../../../../../util/enums";
import { formatInTimeZone } from "../../../../../util/formatInTimezone";
import Button, { Color } from "../../../../../components/Form/Button";
import TextField from "../../../../../components/Form/Input";
import Select from "../../../../../components/Form/Select";
import Modal, { ModalType } from "../../../../../components/Generic/Modal";
import LoadingModal from "../../../../../components/LoadingModal";
import useStyles from "./styles";
import { ImageInput } from "Components/Form";
import { Logo } from "Models/company";
import RadioButton from "Components/Form/RadioGroup";
import { ListContactsCards } from "../ContactSection/ListContactsCards";
import ListLocation from "../ListLocation";
import { Props } from "../../types/types";
import { ListContactsTable } from "../ContactSection/ListContactsTable";
import { useAgentCreateAndEditActions } from "../../hooks/useAgentCreateAndEditActions";
import { AppTimezoneSelect } from "Components/Generic/AppTimezoneSelect";
import { AppBadge } from "Components/AppBadge";
import { Contact } from "Models/contact";
import { Share as ShareIcon } from "iconsax-react";
import { AppConfirmationModal, AppConfirmationModalRef } from "Components/AppConfirmationModal";
import { useDebouncedCallback } from "@mantine/hooks";
import { Box } from "@mui/system";
import { colors } from "@zapalacorp/tokens";
import { handleAgentValidation } from "../../schemas/handleAgentValidation";
import {
  ContactListModal,
  ContactListModalRef,
} from "../ContactSection/ContactListModal/ContactListModal";
import { ContactCreateModal, ContactModalRef } from "../ContactSection/ContactCreateModal";
import { ContactSection } from "../ContactSection/ContactSection";

const createNewNetworkTokenError = (translate: any, token?: string) => {
  return new yup.ValidationError(translate("pleaseEnterAValidToken"), token, "agent.shipeeziToken");
};

const AgentDetailsTab = (props: Props) => {
  const {
    onUpdate,
    onDelete,
    loadingAgent,
    onCreate,
    onInvite,
    viewOnly,
    sequence,
    goBack,
    scrollToTop,
  } = props;

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState<boolean>(false);
  const contactViewModalRef = useRef<ContactListModalRef>(null);
  const contactModalRef = useRef<ContactModalRef>(null);

  const { agent, handleUpdateAgent } = useAgentHook();

  const isCreatingAgent = useMemo(() => !agent?.id, [agent?.id]);

  const { t: translate } = useTranslation();

  const isCompanyConnectionActive = useMemo(() => {
    return agent?.associatedCompany?.status === Status.ACTIVE;
  }, [agent?.associatedCompany]);

  const isCompanyOnShipeeziNetwork = useMemo(() => {
    return agent?.isShipeeziNetwork;
  }, [agent]);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [isValidToken, setIsValidToken] = useState<boolean>(isCompanyConnectionActive);
  const [isManualTokenLoading, setIsManualTokenLoading] = useState<boolean>(false);

  const classes = useStyles();

  const { getTimezoneList, useInviteAgent, useCheckToken, useRevokeConnection } =
    useAgentCreateAndEditActions();

  const { data: timezones } = getTimezoneList();
  const { mutate: inviteAgent, isLoading: isInviteAgentLoading } = useInviteAgent({
    onSuccess() {
      onInvite?.();
    },
  });
  const { mutate: checkToken } = useCheckToken({
    onSuccess({ data }) {
      setIsManualTokenLoading(false);

      if (!data.exists || !data.isActive) {
        setErrors((errors) => [
          ...errors,
          createNewNetworkTokenError(translate, agent?.networkToken),
        ]);
        setIsValidToken(false);
        return;
      }

      if (!isValidToken) setIsValidToken(true);
      setErrors([]);
    },
    onError() {
      setIsManualTokenLoading(false);
    },
  });

  const { mutate: revokeConnection, isLoading: isLoadingRevokeConnection } = useRevokeConnection({
    onSuccess() {
      if (agent?.id) {
        window.location.reload();
      }
    },
  });

  const handleValidateToken = useDebouncedCallback(async (token: string) => {
    if (!token.trim()) {
      setIsManualTokenLoading(false);
      setErrors([]);
      setIsValidToken(false);
      return;
    }

    setErrors([]);
    checkToken({ token });
  }, 500);

  const handleTokenInputChange = (value: string) => {
    handleUpdateAgent("networkToken", value);

    // Se há algum valor, inicia o loading imediatamente
    if (value.trim()) {
      setIsManualTokenLoading(true);
      setIsValidToken(false);
    } else {
      setIsManualTokenLoading(false);
      setIsValidToken(false);
      setErrors([]);
    }

    // Chama a validação com debounce
    handleValidateToken(value);
  };

  const handleClickAddContact = async () => {
    try {
      const result = await contactModalRef.current?.open(agent);

      if (result) {
        const { action, contact: newContact } = result;

        if (action === "save" && newContact) {
          const contactsUpdated = [...(agent.contacts || [])];

          // Se é o primeiro contato, marcar como primário
          if (contactsUpdated.length === 0) {
            newContact.isPrimary = true;
          }

          contactsUpdated.unshift(newContact);
          handleUpdateAgent("contacts", contactsUpdated);
        }
        // Se action === 'cancel', não fazemos nada
      }
    } catch (error) {
      console.error("Error opening contact modal:", error);
    }
  };

  const handleClickEditAgent = async (row?: Contact) => {
    try {
      const result = await contactModalRef.current?.open(agent, row);

      if (result) {
        const { action, contact: updatedContact } = result;

        if (action === "update" && updatedContact) {
          const contactsUpdated = [...agent.contacts];
          const contactIndex = agent.contacts.findIndex(
            (c) =>
              (c.id && c.id === updatedContact.id) ||
              (!updatedContact.id && updatedContact.email === c.email)
          );

          if (contactIndex !== -1) {
            contactsUpdated[contactIndex] = {
              ...contactsUpdated[contactIndex],
              ...updatedContact,
            };
            handleUpdateAgent("contacts", contactsUpdated);
          }
        }
        // Se action === 'cancel', não fazemos nada
      }
    } catch (error) {
      console.error("Error opening contact modal:", error);
    }
  };

  const handleViewAllContacts = () => {
    contactViewModalRef?.current?.open();
  };

  const modalRef = useRef<AppConfirmationModalRef>(null);

  useEffect(() => {
    if (!sequence || agent?.code) return;
    const seqAgent = `${sequence?.prefix ? sequence.prefix : ""}${
      sequence?.nextValue ? sequence?.nextValue : ""
    }`;

    handleUpdateAgent("code", seqAgent);
  }, [sequence]);

  useEffect(() => {
    if (scrollToTop) window.scrollTo(0, 0);
  }, [scrollToTop]);

  const onClickSave = useCallback(
    (params?: { sendInviteAfterCreate?: boolean }) => {
      if (
        handleAgentValidation({
          agent,
          setErrors,
          translate,
          skipContactsNestedValidations: agent?.contacts?.length > 1,
          skipLocationsNestedValidations: agent?.locations?.length > 1,
        })
      ) {
        if (isCreatingAgent) {
          return onCreate?.(params);
        }
        return onUpdate?.();
      }
    },
    [isCreatingAgent, agent, setErrors, translate]
  );

  const handleInviteAgent = async () => {
    if (!agent?.id) return;
    const result: boolean =
      (await modalRef.current?.open({
        title: translate("inviteToNetwork"),
        message: translate("wouldYouLikeToSendAnInviteToAgentName", {
          agentName: agent?.entity || agent?.name || "",
        }),
      })) ?? false;

    if (!result) return;

    inviteAgent({ agent });
  };

  const handleRevoke = async () => {
    if (!agent?.id) return;
    const result: boolean =
      (await modalRef.current?.open({
        title: translate("revokeConnection"),
        message: translate("areYouSureYouWantToRevokeTheConnection"),
      })) ?? false;

    if (!result) return;

    revokeConnection({ agentId: agent?.id, companyId: agent?.associatedCompanyId });
  };

  const renderInputTokenRightIcon = () => {
    // Mostra loading se está validando OR se está no estado manual de loading
    if (isManualTokenLoading) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center">
          <CircularProgress size={20} />
        </Box>
      );
    }

    if (agent?.networkToken && isValidToken) {
      return (
        <Tooltip
          data-cy="action-details"
          title={translate("agentIsConnectedToTheShipeezisNetwork")}
          placement="top"
        >
          <Box display="flex" alignItems="center" justifyContent="center">
            <ShareIcon size={20} color={colors.gray500} />
          </Box>
        </Tooltip>
      );
    }

    return null;
  };

  const renderAgentConnectionSection = () => {
    if (agent?.isUsingNetworkToken) {
      return (
        <Grid container item xs={9} spacing={2} display="flex" alignItems="flex-end">
          <Grid item xs={4}>
            <TextField
              name="agent.shipeeziToken"
              type="text"
              label={translate("agentShipeeziNetworkToken")}
              onChange={(event) => handleTokenInputChange(event.target.value)}
              error={fieldHasError("agent.shipeeziToken")}
              errorMessage={getErrorByField("agent.shipeeziToken")}
              value={agent?.networkToken || ""}
              data-cy="shipeeziToken"
              disabled={viewOnly || isCompanyConnectionActive}
              rightIcon={renderInputTokenRightIcon()}
            />
          </Grid>
          {isCompanyConnectionActive && (
            <Grid item xs={2}>
              <Button
                disabled={viewOnly || loadingAgent || isLoadingRevokeConnection}
                loading={isLoadingRevokeConnection}
                customColor={Color.ERROR}
                data-cy="button-revoke"
                onClick={handleRevoke}
              >
                {translate("revoke")}
              </Button>
            </Grid>
          )}
        </Grid>
      );
    }

    if (isCompanyOnShipeeziNetwork) {
      return (
        <Grid container item xs={9} spacing={2}>
          <Grid item xs={4} display={"flex"} alignItems="center" justifyContent={"flex-start"}>
            <Tooltip
              data-cy="action-details"
              title={translate("agentIsConnectedToTheShipeezisNetwork")}
              placement="top"
            >
              <div>
                <ShareIcon size={20} />
              </div>
            </Tooltip>
          </Grid>
        </Grid>
      );
    }

    if (agent?.id) {
      return (
        <Grid container item xs={9} spacing={2} display="flex" alignItems="flex-end">
          <Grid item xs={2}>
            <Button
              disabled={viewOnly || loadingAgent || isInviteAgentLoading}
              customColor={Color.BRAND}
              loading={isInviteAgentLoading}
              onClick={handleInviteAgent}
              data-cy="button-invite"
            >
              {translate("invite")}
            </Button>
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  const fieldHasError = (field: string, options?: { useRegex?: boolean }): boolean => {
    return !!errors?.find((error) => {
      if (options?.useRegex) {
        try {
          if (!error.path) return false;
          const regex = new RegExp(field);
          return regex.test(error.path);
        } catch (e) {
          console.error("Invalid regex:", field);
          return false;
        }
      } else {
        return error.path === field;
      }
    });
  };
  const getErrorByField = (field: string, options?: { useRegex?: boolean }): string | undefined => {
    const error = errors?.find((error) => {
      if (options?.useRegex) {
        try {
          const regex = new RegExp(field);
          if (!error.path) return false;
          return regex.test(error.path);
        } catch (e) {
          console.error("Regex inválida em getErrorByField:", field);
          return false;
        }
      } else {
        return error.path === field;
      }
    });

    return error?.message;
  };
  const renderDeleteAgentModal = () => {
    return (
      <Modal
        open={deleteModalIsOpen}
        title={`${translate("deleteAgent")}`}
        className={classes.deleteModalWidth}
        showActionButtons
        type={ModalType.DELETE}
        actionButtonProps={{
          customColorCancel: Color.DEFAULT,
          customColorSave: Color.ERROR,
          textSave: translate("delete"),
          textCancel: translate("cancel"),
          loading: loadingAgent,
          onSave: () => {
            if (onDelete) onDelete();
          },
          onCancel: () => setDeleteModalIsOpen(false),
        }}
        handleClose={() => setDeleteModalIsOpen(false)}
      >
        <Grid container direction="column">
          <Grid item className={classes.deleteModalDescription}>
            <span>{translate("doYouReallyWantToDeleteThisAgent")}?</span>
          </Grid>
          <Grid
            item
            className={classnames(
              classes.deleteModalDescription,
              classes.deleteModalDescriptionConfirm
            )}
          >
            <span>{translate("thisActionIsPermanentAndCannotBeUndone")}</span>
          </Grid>
        </Grid>
      </Modal>
    );
  };

  return (
    <div>
      {renderDeleteAgentModal()}
      <Grid container spacing={2} direction="row" className={classes.formContent}>
        <Grid item xs={12}>
          <span className={classes.title}>{translate("agentDetails")}</span>
        </Grid>
        <Grid item xs={12} sm={4}>
          <ImageInput
            id={"image-input"}
            title="Logo"
            logo={agent?.logo}
            onChangeLogo={(logo?: Logo) => {
              handleUpdateAgent("logo", logo ?? null);
              if (!logo && agent?.logoId) handleUpdateAgent("logoId", null);
            }}
            maxAllowedSizeMb={2}
            allowedImageExtensions={["jpg", "jpeg", "gif", "png"]}
            hideFooter={viewOnly}
            disabled={viewOnly}
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          <TextField
            name="agent.code"
            type="text"
            label={`${translate("agentId")}`}
            onChange={(event) => handleUpdateAgent("code", event.target.value)}
            value={agent?.code || ""}
            error={fieldHasError("agentId")}
            errorMessage={getErrorByField("agentId")}
            data-cy="code"
            disabled={viewOnly}
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          <TextField
            name="agent.parentCode"
            type="text"
            label={`${translate("agentParentCode")}`}
            onChange={(event) => handleUpdateAgent("parentCode", event.target.value)}
            value={agent?.parentCode || ""}
            error={fieldHasError("parentCode")}
            errorMessage={getErrorByField("parentCode")}
            data-cy="parentCode"
            disabled={viewOnly}
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          <TextField
            name="agent.established"
            type="date"
            label={translate("establishedYear")}
            onChange={(event) =>
              handleUpdateAgent(
                "established",
                formatInTimeZone(
                  "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
                  "UTC",
                  parseISO(event.target.value)
                )
              )
            }
            value={formatInTimeZone("yyyy-MM-dd", "UTC", agent?.established) ?? ""}
            error={fieldHasError("established")}
            errorMessage={getErrorByField("established")}
            data-cy="established"
            disabled={viewOnly}
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          <Select
            name="agent.status"
            label={translate("status")}
            onChange={(event: SelectChangeEvent<any>) =>
              handleUpdateAgent("status", event.target.value)
            }
            value={agent?.status ?? ""}
            error={fieldHasError("status")}
            errorMessage={getErrorByField("status")}
            data-cy="status"
            disabled={viewOnly}
          >
            <MenuItem value={AgentStatus.ACTIVE}>{translate("active")}</MenuItem>
            <MenuItem value={AgentStatus.DISABLED}>{translate("disabled")}</MenuItem>
          </Select>
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label={translate("entityLegalName")}
            placeholder={"Entity name"}
            onChange={(event) => handleUpdateAgent("entity", event.target.value)}
            value={agent?.entity ?? ""}
            error={fieldHasError("entity")}
            errorMessage={getErrorByField("entity")}
            data-cy="entity"
            required
            disabled={viewOnly}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label={translate("website")}
            placeholder={translate("enterWebsite")}
            onChange={(event) => handleUpdateAgent("website", event.target.value)}
            value={agent?.website ?? ""}
            required
            error={fieldHasError("website")}
            errorMessage={getErrorByField("website")}
            data-cy="website"
            disabled={viewOnly}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label={translate("registrationNumber")}
            placeholder={translate("enterRegistrationNumber")}
            onChange={(event) => handleUpdateAgent("registrationNumber", event.target.value)}
            value={agent?.registrationNumber ?? ""}
            error={fieldHasError("registrationNumber")}
            errorMessage={getErrorByField("registrationNumber")}
            data-cy="registration"
            disabled={viewOnly}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <AppTimezoneSelect
            handleChange={(timezone) => {
              if (!timezone?.id) return;
              handleUpdateAgent("timezone", timezone);
              handleUpdateAgent("timezoneId", timezone.id);
            }}
            hasError={fieldHasError("timezone")}
            errorMessage={getErrorByField("timezone")}
            value={agent?.timezone}
            hasShowAutoUpdateTimezoneByCurrentUserBroswerLabel={false}
            isDisabled={viewOnly}
          />
        </Grid>

        <Grid item xs={3}>
          <RadioButton
            label={translate("doYouHaveAgentShipeeziNetworkKey")}
            required
            options={[
              {
                value: true,
                label: "Yes",
              },
              {
                value: false,
                label: "No",
              },
            ]}
            value={agent?.isUsingNetworkToken}
            onChange={(event) => {
              handleUpdateAgent("isUsingNetworkToken", event.target.value == "true" ? true : false);
            }}
            error={fieldHasError("isUsingNetworkToken")}
            errorMessage={getErrorByField("isUsingNetworkToken")}
            disabled={viewOnly || isCompanyConnectionActive}
          />
        </Grid>

        {renderAgentConnectionSection()}

        <ContactSection
          contacts={agent?.contacts}
          fieldHasError={fieldHasError}
          getErrorByField={getErrorByField}
          onChange={(contactsUpdated) => handleUpdateAgent("contacts", contactsUpdated)}
          onClickAddContact={handleClickAddContact}
          onClickEditContact={handleClickEditAgent}
          onViewContacts={handleViewAllContacts}
          timezones={timezones}
          viewOnly={viewOnly}
        />

        <Grid item xs={12}>
          <span className={classes.title}>{translate("location")}</span>

          <AppBadge
            variant="light-gray"
            paddingLeft="12px"
            paddingRight="12px"
            paddingTop="4px"
            paddingBottom="4px"
            sx={{ fontSize: "12px", marginLeft: "10px" }}
          >
            {agent?.locations?.length ?? 0}
          </AppBadge>

          {/* Mostrar erros de validação de locations */}
          {fieldHasError("locations") && (
            <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
              {getErrorByField("locations")}
            </div>
          )}
          {fieldHasError("^locations\\[\\d+\\]\\..+", { useRegex: true }) && (
            <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
              {getErrorByField("^locations\\[\\d+\\]\\..+", { useRegex: true })}
            </div>
          )}
        </Grid>

        <Grid item xs={12}>
          <ListLocation
            viewOnly={viewOnly}
            locations={agent?.locations as Location[]}
            contacts={agent?.contacts as Contact[]}
            onChange={(locations) => handleUpdateAgent("locations", locations)}
          />
        </Grid>

        <Grid item container xs={12} justifyContent="end">
          <Grid item>
            <Button
              customColor={Color.CANCEL}
              data-cy="agent-cancel-button"
              onClick={() => goBack && goBack()}
              disabled={loadingAgent}
            >
              {translate("cancel")}
            </Button>

            <Button
              disabled={viewOnly || loadingAgent}
              onClick={() => onClickSave()}
              customColor={Color.SUCCESS}
              data-cy="button-save"
              className={classes.buttonSave}
            >
              {agent?.id ? translate("save") : translate("create")}
            </Button>

            {console.log("isCompanyConnectionActive", isCompanyConnectionActive)}
            {console.log("agent?.id", agent?.id)}
            {console.log("agent?.isUsingNetworkToken", agent?.isUsingNetworkToken)}
            {!isCompanyConnectionActive && !agent?.isUsingNetworkToken && (
              <Button
                disabled={viewOnly || loadingAgent}
                onClick={() => onClickSave({ sendInviteAfterCreate: true })}
                customColor={Color.SUCCESS}
                data-cy="button-save"
                className={classes.buttonSave}
              >
                {agent?.id ? translate("saveAndInvite") : translate("createAndInvite")}
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>

      <LoadingModal showing={loadingAgent} />

      <ContactListModal
        ref={contactViewModalRef}
        timezones={timezones}
        contacts={agent?.contacts}
        onChange={(contacts) => handleUpdateAgent("contacts", contacts)}
        onClickEditContact={handleClickEditAgent}
      />

      <ContactCreateModal ref={contactModalRef} timezones={timezones} />

      <AppConfirmationModal ref={modalRef} maxWidth="392px" />
    </div>
  );
};

export { AgentDetailsTab };
